import React from 'react';
import { AIInsight } from '../../types';
import Card from '../common/Card';
import { motion } from 'framer-motion';
import {
  HiTrendingUp,
  HiExclamationCircle,
  HiLightBulb,
  HiChartBar,
  HiSparkles,
} from 'react-icons/hi';
import { formatDistanceToNow } from 'date-fns';

interface AIInsightsProps {
  insights: AIInsight[];
}

const AIInsights: React.FC<AIInsightsProps> = ({ insights }) => {
  const getIcon = (type: AIInsight['type']) => {
    const iconClass = 'w-6 h-6';
    switch (type) {
      case 'trend':
        return <HiTrendingUp className={iconClass} />;
      case 'anomaly':
        return <HiExclamationCircle className={iconClass} />;
      case 'recommendation':
        return <HiLightBulb className={iconClass} />;
      case 'forecast':
        return <HiChartBar className={iconClass} />;
      default:
        return <HiSparkles className={iconClass} />;
    }
  };

  const getTypeColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'trend':
        return 'from-blue-500 to-cyan-600';
      case 'anomaly':
        return 'from-orange-500 to-red-600';
      case 'recommendation':
        return 'from-purple-500 to-pink-600';
      case 'forecast':
        return 'from-green-500 to-teal-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getImpactBadge = (impact: AIInsight['impact']) => {
    const baseClass = 'badge';
    switch (impact) {
      case 'high':
        return `${baseClass} badge-danger`;
      case 'medium':
        return `${baseClass} badge-warning`;
      case 'low':
        return `${baseClass} badge-info`;
      default:
        return `${baseClass} badge-gray`;
    }
  };

  return (
    <Card
      title="AI-Powered Insights"
      subtitle="Automated analysis and recommendations"
      action={
        <div className="flex items-center gap-2">
          <HiSparkles className="w-5 h-5 text-primary-500" />
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {insights.length} Active Insights
          </span>
        </div>
      }
    >
      <div className="space-y-4">
        {insights.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-dark-800 flex items-center justify-center mb-4">
              <HiSparkles className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              No insights available yet
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Check back later for AI-generated recommendations
            </p>
          </div>
        ) : (
          insights.map((insight, index) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="group relative bg-gradient-to-br from-gray-50 to-white dark:from-dark-800 dark:to-dark-900 rounded-xl p-4 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${getTypeColor(
                    insight.type
                  )} flex items-center justify-center text-white shadow-lg transform transition-transform group-hover:scale-110 group-hover:rotate-6`}
                >
                  {getIcon(insight.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                      {insight.title}
                    </h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={getImpactBadge(insight.impact)}>
                        {insight.impact} impact
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    {insight.description}
                  </p>

                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Confidence:</span>
                        <span className={`font-semibold ${
                          insight.confidence >= 80
                            ? 'text-success-600 dark:text-success-400'
                            : insight.confidence >= 60
                            ? 'text-warning-600 dark:text-warning-400'
                            : 'text-gray-500'
                        }`}>
                          {insight.confidence}%
                        </span>
                      </span>
                      <span className="text-gray-300 dark:text-dark-600">•</span>
                      <span>
                        {formatDistanceToNow(new Date(insight.timestamp), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>

                    {insight.actionable && (
                      <button className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex items-center gap-1">
                        <span>Take Action</span>
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Confidence Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 dark:bg-dark-800 rounded-b-xl overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    insight.confidence >= 80
                      ? 'bg-success-500'
                      : insight.confidence >= 60
                      ? 'bg-warning-500'
                      : 'bg-gray-400'
                  }`}
                  style={{ width: `${insight.confidence}%` }}
                />
              </div>
            </motion.div>
          ))
        )}
      </div>
    </Card>
  );
};

export default AIInsights;
